import React from 'react';
import { connect } from 'react-redux';
import { AppState } from '../store/state';
import {
  selectAssignmentState,
  selectConference,
  selectCurrentRole,
  selectFormState,
  selectUserPermissions,
} from '../store/selectors';
import { ConferenceState, Role } from '../store/conference/types';
import { RoleTree, doesHandleReviewers } from '../helpers/role';
import { getRoleName } from '../helpers/translations';
import { useTranslation } from 'react-i18next';
import { Review } from '../store/review/types';
import { PermissionString } from '../store/permission/types';
import { Form, FormState } from '../store/form/types';
import styled from 'styled-components';

interface Props {
  currentRole: Role | null;
  conferenceState: ConferenceState;
  assignments: Review[];
  permissions: PermissionString[];
  formState: FormState;
}

const DebugPage: React.FC<Props> = ({ currentRole, conferenceState, assignments, permissions, formState }) => {
  const { t } = useTranslation();

  const roles = Object.values(conferenceState.roleById);
  const roleTree = new RoleTree(roles);

  const descendantRoles = currentRole ? roleTree.descendantsOf(currentRole.id) : [];

  const getFormVisibilities = (assignmentRoleId: number): React.ReactNode => {
    if (!currentRole) return '-';
    const { formById, visibilityByFormId } = formState;
    const parts: { name: string; visibility: string; toRole: string | null }[] = [];
    Object.values(formById).forEach((form: Form) => {
      const roleVisibilities = visibilityByFormId[form.id]?.[currentRole.id];
      if (!roleVisibilities) return;
      roleVisibilities.forEach((v) => {
        if (v.to_role_id === assignmentRoleId || v.to_role_id === null) {
          const toRoleName = v.to_role_id
            ? conferenceState.roleById[v.to_role_id]?.description ?? String(v.to_role_id)
            : null;
          parts.push({ name: form.name, visibility: v.visibility, toRole: toRoleName });
        }
      });
    });
    if (parts.length === 0) return '-';
    return parts.map((p, i) => (
      <span key={i}>
        {i > 0 && ', '}
        {p.name}: <strong>{p.visibility}</strong>
        {p.toRole && <span className="text-gray-500"> → {p.toRole}</span>}
      </span>
    ));
  };

  return (
    <StyledWrapper className="p-6">
      <h2 className="text-xl font-bold mb-6">{t('Debug')}</h2>

      <Section>
        <h3 className="text-base font-semibold mb-3">Current Role</h3>
        {currentRole ? (
          <table className="table-auto border-collapse w-full max-w-xl">
            <tbody>
              <Row label="Role ID" value={currentRole.id} />
              <Row label="Role name" value={getRoleName(currentRole, t)} />
              <Row label="Role type" value={currentRole.type} />
              <Row label="Rank" value={currentRole.rank} />
              <Row label="Handles reviewers" value={doesHandleReviewers(currentRole) ? 'Yes' : 'No'} />
              <Row label="Parent role_id" value={currentRole.role_id ?? 'null'} />
              <Row label="Track ID" value={currentRole.track_id} />
            </tbody>
          </table>
        ) : (
          <p className="text-gray-500">No role selected</p>
        )}
      </Section>

      <Section>
        <h3 className="text-base font-semibold mb-3">Permissions ({permissions.length})</h3>
        {permissions.length > 0 ? (
          <ul className="list-disc ml-6">
            {permissions.map((p) => (
              <li key={p} className="text-sm font-mono">
                {p}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500">No permissions</p>
        )}
      </Section>

      <Section>
        <h3 className="text-base font-semibold mb-3">All Track Roles</h3>
        <table className="table-auto border-collapse w-full max-w-2xl">
          <thead>
            <tr>
              <Th>ID</Th>
              <Th>Name</Th>
              <Th>Type</Th>
              <Th>Rank</Th>
              <Th>role_id (supervises)</Th>
            </tr>
          </thead>
          <tbody>
            {roles.map((role) => (
              <tr key={role.id} className={role.id === currentRole?.id ? 'bg-blue-50' : ''}>
                <Td>{role.id}</Td>
                <Td>{getRoleName(role, t)}</Td>
                <Td>{role.type}</Td>
                <Td>{role.rank}</Td>
                <Td>{role.role_id ?? '-'}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {doesHandleReviewers(currentRole!) && (
        <Section>
          <h3 className="text-base font-semibold mb-3">Descendant Roles (can supervise)</h3>
          {descendantRoles.length > 0 ? (
            <ul className="list-disc ml-6">
              {descendantRoles.map((role) => (
                <li key={role.id}>
                  {getRoleName(role, t)} (ID: {role.id}, type: {role.type})
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-500">None</p>
          )}
        </Section>
      )}

      <Section>
        <h3 className="text-base font-semibold mb-3">Assignments ({assignments.length})</h3>
        {assignments.length > 0 ? (
          <table className="table-auto border-collapse w-full">
            <thead>
              <tr>
                <Th>ID</Th>
                <Th>Submission</Th>
                <Th>Role ID</Th>
                <Th>Role</Th>
                <Th>Person Role ID</Th>
                <Th>Person</Th>
                <Th>Status</Th>
                <Th>Form Visibility</Th>
              </tr>
            </thead>
            <tbody>
              {assignments.map((a) => (
                <tr key={a.id}>
                  <Td>{a.id}</Td>
                  <Td>{a.submission_id}</Td>
                  <Td>{a.role_id}</Td>
                  <Td>{conferenceState.roleById[a.role_id]?.description ?? '-'}</Td>
                  <Td>{a.person_role_id}</Td>
                  <Td>{a.person?.full_name ?? '-'}</Td>
                  <Td>
                    <span className={a.validation_status === 'Complete' ? 'text-green-600' : 'text-orange-500'}>
                      {a.validation_status}
                    </span>
                  </Td>
                  <Td className="text-xs">{getFormVisibilities(a.role_id)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-gray-500">No assignments loaded</p>
        )}
      </Section>
    </StyledWrapper>
  );
};

const Row: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <tr>
    <td className="pr-4 py-1 font-medium text-gray-600">{label}</td>
    <td className="py-1">{String(value)}</td>
  </tr>
);

const mapStateToProps = (state: AppState) => ({
  currentRole: selectCurrentRole(state),
  conferenceState: selectConference(state),
  assignments: selectAssignmentState(state).assignments,
  permissions: selectUserPermissions(state),
  formState: selectFormState(state),
});

export default connect(mapStateToProps)(DebugPage);

const StyledWrapper = styled.div`
  overflow-y: auto;
  height: 100%;
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 0.5rem;
  border-bottom: 2px solid #e5e7eb;
  font-size: 0.875rem;
  color: #6b7280;
`;

const Td = styled.td`
  padding: 0.5rem;
  border-bottom: 1px solid #f3f4f6;
  font-size: 0.875rem;
`;
